<template>
  <div>
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mt-5 pt-5 ps-3">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item">
          <router-link to="/">Home</router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link to="/admin/knowledge-bases">Knowledge Bases</router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">
          {{ knowledgeBase?.name || "Details" }}
        </li>
      </ol>
    </nav>

    <div class="container-fluid">
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading knowledge base...</p>
      </div>

      <!-- Knowledge Base Details -->
      <div v-else-if="knowledgeBase">
        <!-- Summary Card -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <div class="row align-items-center">
                  <div class="col-md-8">
                    <h4 class="mb-2">
                      {{ knowledgeBase.name }}
                      <span
                        :class="[
                          'badge',
                          'ms-2',
                          knowledgeBase.is_active ?? knowledgeBase.isActive
                            ? 'bg-success'
                            : 'bg-secondary',
                        ]"
                      >
                        {{
                          knowledgeBase.is_active ?? knowledgeBase.isActive
                            ? "Active"
                            : "Inactive"
                        }}
                      </span>
                      <span v-if="knowledgeBase.category" class="badge bg-info ms-2 text-capitalize">
                        {{ knowledgeBase.category?.name }}
                      </span>
                    </h4>
                    <p class="mb-3">{{ knowledgeBase.description }}</p>
                    <div class="d-flex gap-3 flex-wrap">
                      <span>
                        <i class="fa fa-file-text me-1 text-primary"></i>
                        <strong>Documents:</strong>
                        {{ documents.length }}
                      </span>
                      <span>
                        <i class="fa fa-calendar me-1 text-info"></i>
                        <strong>Created:</strong>
                        {{
                          formatDate(
                            knowledgeBase.created_at || knowledgeBase.createdAt
                          )
                        }}
                      </span>
                    </div>
                  </div>
                  <div class="col-md-4 text-md-end ">
                    <div class="d-flex align-items-start justify-content-end gap-2">
                   
                    <router-link
                      to="/admin/knowledge-bases"
                      class="btn btn-outline-secondary btn-sm"
                    >
                      <i class="fa fa-arrow-left me-1"></i>
                      Back to List
                    </router-link>
                     <router-link
                      :to="`/admin/knowledge-bases/${knowledgeBaseId}/edit`"
                      class="btn btn-primary btn-sm"
                    >
                      <i class="fa fa-edit me-1"></i>
                      Edit
                    </router-link>
                    </div>
                   
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <ul class="nav nav-tabs card-header-tabs" role="tablist">
                  <li class="nav-item" role="presentation">
                    <button
                      class="nav-link"
                      :class="{ active: activeTab === 'documents' }"
                      @click="activeTab = 'documents'"
                      type="button"
                    >
                      <i class="fa fa-file-text me-1"></i>
                      Documents
                      <span
                        v-if="documents.length > 0"
                        class="badge bg-primary ms-1"
                      >
                        {{ documents.length }}
                      </span>
                    </button>
                  </li>
                  <li class="nav-item" role="presentation">
                    <button
                      class="nav-link"
                      :class="{ active: activeTab === 'retrieval-test' }"
                      @click="activeTab = 'retrieval-test'"
                      type="button"
                    >
                      <i class="fa fa-search me-1"></i>
                      Retrieval Test
                    </button>
                  </li>
                </ul>
              </div>

              <div class="card-body">
                <!-- Documents Tab -->
                <div v-if="activeTab === 'documents'">
                  <!-- Redesigned Upload Section -->
                  <div class="card mb-4 border-0 shadow-sm overflow-hidden">
                    <div class="card-header bg-white border-bottom-0 pt-4 pb-0">
                      <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <h6 class="mb-0 fw-bold">
                          <i class="fa fa-plus-circle text-primary me-2"></i>
                          Add Knowledge Base Content
                        </h6>
                        <div class="btn-group btn-group-sm p-1 bg-light rounded-pill">
                          <button 
                            type="button" 
                            class="btn rounded-pill px-3" 
                            :class="uploadMethod === 'file' ? 'btn-primary shadow-sm' : 'btn-light border-0 text-muted'"
                            @click="uploadMethod = 'file'"
                          >
                            <i class="fa fa-file-upload me-1"></i> Upload File
                          </button>
                          <button 
                            type="button" 
                            class="btn rounded-pill px-3" 
                            :class="uploadMethod === 'url' ? 'btn-primary shadow-sm' : 'btn-light border-0 text-muted'"
                            @click="uploadMethod = 'url'"
                          >
                            <i class="fa fa-link me-1"></i> Add URL
                          </button>
                          <button 
                            type="button" 
                            class="btn rounded-pill px-3" 
                            :class="uploadMethod === 'text' ? 'btn-primary shadow-sm' : 'btn-light border-0 text-muted'"
                            @click="uploadMethod = 'text'"
                          >
                            <i class="fa fa-text-width me-1"></i> Add Text
                          </button>
                           <button 
                            type="button" 
                            class="btn rounded-pill px-3"
                            :class="uploadMethod === 'prompt' ? 'btn-primary shadow-sm' : 'btn-light border-0 text-muted'"
                            @click="uploadMethod = 'prompt'"
                          >
                            <i class="fa fa-terminal me-1"></i> Add Prompt
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="card-body p-4">
                      <!-- File Upload UI -->
                      <div v-if="uploadMethod === 'file'" class="upload-zone transition-fade">
                        <div class="row g-3">
                          <div class="col-lg-8">
                            <div 
                              class="custom-drop-zone d-flex align-items-center px-4"
                              @click="$refs.fileInputRef.click()"
                            >
                              <div class="d-flex align-items-center w-100">
                                <i class="fa fa-file-upload fa-2x text-primary me-3"></i>
                                <div class="flex-grow-1 overflow-hidden">
                                  <div v-if="selectedFile" class="text-truncate fw-bold">
                                    {{ selectedFile.name }}
                                  </div>
                                  <div v-else class="text-muted">
                                    Click to browse or drag and drop your file here
                                  </div>
                                  <div class="d-flex gap-2 mt-1">
                                    <span class="badge bg-light text-primary border-primary-light small-badge">PDF</span>
                                    <span class="badge bg-light text-primary border-primary-light small-badge">DOCX</span>
                                    <span class="badge bg-light text-primary border-primary-light small-badge">TXT</span>
                                  </div>
                                </div>
                                <button v-if="selectedFile" type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click.stop="selectedFile = null; $refs.fileInputRef.value = ''">
                                  <i class="fa fa-times-circle"></i>
                                </button>
                              </div>
                              <input
                                type="file"
                                ref="fileInputRef"
                                @change="handleFileSelect"
                                accept=".pdf,.docx,.txt"
                                class="d-none"
                              />
                            </div>
                          </div>
                          <div class="col-lg-4">
                            <button
                              @click="handleFileUpload"
                              class="btn btn-primary w-100 shadow-hover d-flex align-items-center justify-content-center unit-height"
                              :disabled="!selectedFile || isUploadingFile"
                            >
                              <span v-if="isUploadingFile">
                                <span class="spinner-border spinner-border-sm me-2"></span>
                                Uploading...
                              </span>
                              <span v-else>
                                <i class="fa fa-cloud-upload-alt me-2"></i>
                                Start Upload
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- URL Input UI -->
                      <div v-if="uploadMethod === 'url'" class="url-zone transition-fade">
                        <div class="row g-3">
                          <div class="col-md-5">
                            <div class="input-wrapper">
                              <div class="input-group">
                                <span class="input-group-text bg-white border-end-0 border-light-2">
                                  <i class="fa fa-link text-muted"></i>
                                </span>
                                <input
                                  type="url"
                                  v-model="urlInput"
                                  class="form-control border-start-0 border-light-2 unit-height"
                                  placeholder="https://example.com/article"
                                />
                              </div>
                            </div>
                          </div>
                          <div class="col-md-4">
                            <div class="input-group">
                              <span class="input-group-text bg-white border-end-0 border-light-2">
                                <i class="fa fa-tag text-muted"></i>
                              </span>
                              <input
                                type="text"
                                v-model="urlTitle"
                                class="form-control border-start-0 border-light-2 unit-height"
                                placeholder="Title (optional)"
                              />
                            </div>
                          </div>
                          <div class="col-md-3">
                            <button
                              @click="handleAddUrl"
                              class="btn btn-primary w-100 shadow-hover unit-height d-flex align-items-center justify-content-center"
                              :disabled="!urlInput || isAddingUrl"
                            >
                              <span v-if="isAddingUrl">
                                <span class="spinner-border spinner-border-sm me-2"></span>
                                Adding...
                              </span>
                              <span v-else>
                                <i class="fa fa-plus-circle me-2"></i> Add URL
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Text Input UI (Redesigned for consistency) -->
                      <div v-if="uploadMethod === 'text'" class="text-zone transition-fade">
                        <div class="row g-3">
                          <div class="col-lg-8">
                            <div class="custom-drop-zone d-flex align-items-center px-4 mb-0 unit-height">
                              <div class="d-flex align-items-center w-100">
                                <i class="fa fa-file-alt fa-2x text-primary me-3"></i>
                                <div class="flex-grow-1 overflow-hidden">
                                  <div class="fw-bold text-dark">Manually Add Text Content</div>
                                  <div class="text-muted small">Perfect for FAQs, policies, and custom notes</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="col-lg-4">
                            <button
                              @click="showAddTextModal"
                              class="btn btn-primary w-100 shadow-hover d-flex align-items-center justify-content-center unit-height"
                            >
                              <i class="fa fa-edit me-2"></i>
                              Open Text Editor
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Prompt Input UI -->
                      <!-- Prompt Input UI (Redesigned for consistency) -->
                      <div v-if="uploadMethod === 'prompt'" class="prompt-zone transition-fade">
                        <div class="row g-3">
                          <div class="col-lg-8">
                            <div class="custom-drop-zone d-flex align-items-center px-4 mb-0 unit-height">
                              <div class="d-flex align-items-center w-100">
                                <i class="fa fa-terminal fa-2x text-primary me-3"></i>
                                <div class="flex-grow-1 overflow-hidden">
                                  <div class="fw-bold text-dark">Generate from Prompt</div>
                                  <div class="text-muted small">Enter a title and prompt to generate knowledge base content</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="col-lg-4">
                            <button
                              @click="showAddPromptModal"
                              class="btn btn-primary w-100 shadow-hover d-flex align-items-center justify-content-center unit-height"
                            >
                              <i class="fa fa-magic me-2"></i>
                              Open Prompt Editor
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Documents List -->
                  <div v-if="isLoadingDocuments" class="text-center py-4">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  </div>

                  <div
                    v-else-if="documents.length === 0"
                    class="text-center py-5"
                  >
                    <i class="fa fa-folder-open fa-3x text-muted mb-3"></i>
                    <p class="text-muted">No documents added yet</p>
                    <small class="text-muted">
                      Upload files, add URLs, or paste text to populate this
                      knowledge base
                    </small>
                  </div>

                  <div v-else class="table-responsive theme-scrollbar">
                    <table class="table table-hover align-middle">
                      <thead class="table-dark">
                        <tr>
                          <th>Title</th>
                          <th>Type</th>
                          <th>Status</th> 
                          <th>Added On</th>
                          <th class="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="doc in documents" :key="doc._id || doc.id">
                          <td>
                            <strong>{{ doc.title }}</strong>
                          </td>
                          <td>
                            <span
                              :class="[
                                'badge',
                                getSourceTypeBadgeClass(
                                  doc.source_type || doc.sourceType
                                ),
                              ]"
                            >
                              {{ doc.source_type || doc.sourceType }}
                            </span>
                          </td>
                          <td>
                            <span
                              :class="[
                                'badge',
                                getStatusBadgeClass(doc.status),
                              ]"
                            >
                              {{ doc.status }}
                            </span>
                          </td>
                          
                          <td>
                            <small>{{
                              formatDate(doc.created_at || doc.createdAt)
                            }}</small>
                          </td>
                          <td class="text-end">
                            <button
                              @click="viewDocument(doc)"
                              class="btn btn-outline-primary btn-sm me-1"
                              title="View Details"
                            >
                              <i class="fa fa-eye"></i>
                            </button>
                            <button
                              @click="confirmDeleteDocument(doc)"
                              class="btn btn-outline-danger btn-sm"
                              title="Delete"
                            >
                              <i class="fa fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Retrieval Test Tab -->
                <div v-if="activeTab === 'retrieval-test'">
                  <div class="row">
                    <div class="col-lg-8 mx-auto">
                      <div class="mb-4">
                        <h6 class="mb-3">Test Knowledge Base Retrieval</h6>
                        <p class="text-muted">
                          Enter a query to test how well the knowledge base
                          retrieves relevant information
                        </p>

                        <div class="mb-3">
                          <label for="testQuery" class="form-label">
                            Query
                          </label>
                          <textarea
                            id="testQuery"
                            v-model="testQuery"
                            class="form-control"
                            rows="3"
                            placeholder="Enter your query here..."
                          ></textarea>
                        </div>

                        <div class="row mb-3">
                          <div class="col-md-6">
                            <label for="topK" class="form-label">
                              Top K Results
                            </label>
                            <input
                              type="number"
                              id="topK"
                              v-model.number="topK"
                              class="form-control"
                              min="1"
                              max="20"
                              placeholder="5"
                            />
                          </div>
                        </div>

                        <button
                          @click="handleTestRetrieval"
                          class="btn btn-primary"
                          :disabled="!testQuery || isTesting"
                        >
                          <span v-if="isTesting">
                            <span
                              class="spinner-border spinner-border-sm me-1"
                            ></span>
                            Testing...
                          </span>
                          <span v-else>
                            <i class="fa fa-search me-1"></i>
                            Test Retrieval
                          </span>
                        </button>
                      </div>

                      <!-- Test Results -->
                      <div v-if="testResults.length > 0" class="mt-4">
                        <h6 class="mb-3">Results ({{ testResults.length }})</h6>
                        <div
                          v-for="(result, index) in testResults"
                          :key="index"
                          class="card mb-3"
                        >
                          <div class="card-body">
                            <div class="d-flex justify-content-between mb-2">
                              <h6 class="mb-0">
                                {{
                                  result.document_title ||
                                  result.title ||
                                  "Document"
                                }}
                              </h6>
                              <span class="badge bg-success">
                                Score: {{ formatScore(result.score) }}
                              </span>
                            </div>
                            <p class="text-muted small mb-2">
                              <i class="fa fa-file me-1"></i>
                              {{ result.source_type || result.sourceType }}
                            </p>
                            <div class="retrieval-content">
                              {{ result.content || result.chunk_text }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        v-else-if="testResults.length === 0 && hasTestedOnce"
                        class="alert alert-info"
                      >
                        <i class="fa fa-info-circle me-2"></i>
                        No results found for your query
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else class="text-center py-5">
        <i class="fa fa-exclamation-triangle fa-3x text-danger mb-3"></i>
        <p class="text-danger">Failed to load knowledge base</p>
        <router-link to="/admin/knowledge-bases" class="btn btn-primary">
          Back to Knowledge Bases
        </router-link>
      </div>
    </div>

    <!-- Add Text Modal -->
    <div
      class="modal fade"
      id="addTextModal"
      tabindex="-1"
      ref="addTextModalRef"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Add Text Content</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="textTitle" class="form-label">
                Title <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                id="textTitle"
                v-model="textTitle"
                class="form-control"
                placeholder="Enter a title for this content"
              />
            </div>
            <div class="mb-3">
              <label for="textContent" class="form-label">
                Content <span class="text-danger">*</span>
              </label>
              <textarea
                id="textContent"
                v-model="textContent"
                class="form-control"
                rows="10"
                placeholder="Paste or type your content here..."
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="handleAddText"
              :disabled="!textTitle || !textContent || isAddingText"
            >
              <span v-if="isAddingText">
                <span
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                ></span>
                Adding...
              </span>
              <span v-else>
                <i class="fa fa-plus me-1"></i>
                Add Content
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Prompt Modal -->
    <div
      class="modal fade"
      id="addPromptModal"
      tabindex="-1"
      ref="addPromptModalRef"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Generate Content from Prompt</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="promptTitle" class="form-label">
                Title <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                id="promptTitle"
                v-model="promptTitle"
                class="form-control"
                placeholder="Enter a title for this generated content"
              />
            </div>
            <div class="mb-3">
              <label for="promptTextContent" class="form-label">
                Prompt <span class="text-danger">*</span>
              </label>
              <textarea
                id="promptTextContent"
                v-model="promptInput"
                class="form-control"
                rows="6"
                placeholder="E.g., Create a knowledge base for a software engineering team focusing on high-level architecture..."
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="handleGeneratePrompt"
              :disabled="!promptTitle || !promptInput || isGeneratingPrompt"
            >
              <span v-if="isGeneratingPrompt">
                <span
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                ></span>
                Generating...
              </span>
              <span v-else>
                <i class="fa fa-magic me-1"></i>
                Generate
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Document Confirmation Modal -->
    <div
      class="modal fade"
      id="deleteDocumentModal"
      tabindex="-1"
      ref="deleteDocumentModalRef"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Delete</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <p>
              Are you sure you want to delete
              <strong>{{ documentToDelete?.title }}</strong
              >?
            </p>
            <p class="text-danger">
              <i class="fa fa-exclamation-triangle me-1"></i>
              This action cannot be undone.
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="deleteDocument"
              :disabled="isDeletingDocument"
            >
              <span v-if="isDeletingDocument">
                <span
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                ></span>
                Deleting...
              </span>
              <span v-else>
                <i class="fa fa-trash me-1"></i>
                Delete
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import apiService from "@/services/apiService";
import { Modal } from "bootstrap";

// Router
const router = useRouter();
const route = useRoute();

// Computed
const knowledgeBaseId = computed(() => route.params.id as string);

// Data
const knowledgeBase = ref<any>(null);
const documents = ref<any[]>([]);
const isLoading = ref(false);
const isLoadingDocuments = ref(false);
const activeTab = ref("documents");
const uploadMethod = ref("file"); // 'file', 'url', 'text'

// File upload
const fileInputRef = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const isUploadingFile = ref(false);

// URL input
const urlInput = ref("");
const urlTitle = ref("");
const isAddingUrl = ref(false);

// Text modal
const addTextModalRef = ref<HTMLElement | null>(null);
const textTitle = ref("");
const textContent = ref("");
const isAddingText = ref(false);

// Prompt modal/input
const addPromptModalRef = ref<HTMLElement | null>(null);
const promptTitle = ref("");
const promptInput = ref("");
const isGeneratingPrompt = ref(false);

// Delete document
const deleteDocumentModalRef = ref<HTMLElement | null>(null);
const documentToDelete = ref<any>(null);
const isDeletingDocument = ref(false);

// Retrieval test
const testQuery = ref("");
const topK = ref(5);
const testResults = ref<any[]>([]);
const isTesting = ref(false);
const hasTestedOnce = ref(false);

// Methods
const loadKnowledgeBase = async () => {
  isLoading.value = true;
  try {
    const response = await apiService.getKnowledgeBaseById(
      knowledgeBaseId.value
    );
    knowledgeBase.value = response;
  } catch (err: any) {
    console.error("Error loading knowledge base:", err);
    showToast(
      err?.response?.data?.message || "Failed to load knowledge base",
      "error"
    );
  } finally {
    isLoading.value = false;
  }
};

const loadDocuments = async () => {
  isLoadingDocuments.value = true;
  try {
    const response = await apiService.getKnowledgeBaseDocuments(
      knowledgeBaseId.value
    );
    documents.value = response.data || response.documents || [];
  } catch (err: any) {
    console.error("Error loading documents:", err);
    showToast(
      err?.response?.data?.message || "Failed to load documents",
      "error"
    );
  } finally {
    isLoadingDocuments.value = false;
  }
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    selectedFile.value = target.files[0];
  }
};

const handleFileUpload = async () => {
  if (!selectedFile.value) return;

  isUploadingFile.value = true;
  try {
    await apiService.uploadKnowledgeBaseDocument(
      knowledgeBaseId.value,
      selectedFile.value
    );
    showToast("File uploaded successfully", "success");

    // Reset
    selectedFile.value = null;
    if (fileInputRef.value) {
      fileInputRef.value.value = "";
    }

    // Reload documents
    loadDocuments();
  } catch (err: any) {
    console.error("Error uploading file:", err);
    showToast(err?.response?.data?.message || "Failed to upload file", "error");
  } finally {
    isUploadingFile.value = false;
  }
};

const handleAddUrl = async () => {
  if (!urlInput.value) return;

  isAddingUrl.value = true;
  try {
    await apiService.addKnowledgeBaseUrl(
      knowledgeBaseId.value,
      urlInput.value,
      urlTitle.value || undefined
    );
    showToast("URL added successfully", "success");

    // Reset
    urlInput.value = "";
    urlTitle.value = "";

    // Reload documents
    loadDocuments();
  } catch (err: any) {
    console.error("Error adding URL:", err);
    showToast(err?.response?.data?.message || "Failed to add URL", "error");
  } finally {
    isAddingUrl.value = false;
  }
};

const showAddTextModal = () => {
  const modalElement = addTextModalRef.value;
  if (modalElement) {
    const modal = new Modal(modalElement);
    modal.show();
  }
};

const handleAddText = async () => {
  if (!textTitle.value || !textContent.value) return;

  isAddingText.value = true;
  try {
    await apiService.addKnowledgeBaseText(
      knowledgeBaseId.value,
      textTitle.value,
      textContent.value
    );
    showToast("Text content added successfully", "success");

    // Close modal
    const modalElement = addTextModalRef.value;
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    // Reset
    textTitle.value = "";
    textContent.value = "";

    // Reload documents
    loadDocuments();
  } catch (err: any) {
    console.error("Error adding text:", err);
    showToast(err?.response?.data?.message || "Failed to add text", "error");
  } finally {
    isAddingText.value = false;
  }
};

const showAddPromptModal = () => {
  const modalElement = addPromptModalRef.value;
  if (modalElement) {
    const modal = new Modal(modalElement);
    modal.show();
  }
};

const handleGeneratePrompt = async () => {
  if (!promptTitle.value || !promptInput.value) return;  
  isGeneratingPrompt.value = true;
  try {
    await apiService.generateKnowledgeBasePrompt(
      knowledgeBaseId.value,
      {
        title: promptTitle.value,
        prompt: promptInput.value, 
      }
    );
    showToast("Knowledge base generation started successfully", "success");

    // Close modal
    const modalElement = addPromptModalRef.value;
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    // Reset
    promptTitle.value = "";
    promptInput.value = "";
    
    loadDocuments();
  } catch (err: any) {
    console.error("Error generating knowledge base from prompt:", err);
    showToast(err?.response?.data?.message || "Failed to generate knowledge base", "error");
  } finally {
    isGeneratingPrompt.value = false;
  }
};

const viewDocument = (doc: any) => {
  // You can implement a modal to show document details
  alert(`Document: ${doc.title}\nStatus: ${doc.status}`);
};

const confirmDeleteDocument = (doc: any) => {
  documentToDelete.value = doc;
  const modalElement = deleteDocumentModalRef.value;
  if (modalElement) {
    const modal = new Modal(modalElement);
    modal.show();
  }
};

const deleteDocument = async () => {
  if (!documentToDelete.value) return;

  isDeletingDocument.value = true;
  try {
    await apiService.deleteKnowledgeBaseDocument(
      knowledgeBaseId.value,
      documentToDelete.value._id || documentToDelete.value.id
    );
    showToast("Document deleted successfully", "success");

    // Close modal
    const modalElement = deleteDocumentModalRef.value;
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    documentToDelete.value = null;
    loadDocuments();
  } catch (err: any) {
    console.error("Error deleting document:", err);
    showToast(
      err?.response?.data?.message || "Failed to delete document",
      "error"
    );
  } finally {
    isDeletingDocument.value = false;
  }
};

const handleTestRetrieval = async () => {
  if (!testQuery.value) return;

  isTesting.value = true;
  hasTestedOnce.value = true;
  try {
    const response = await apiService.testKnowledgeBaseRetrieval(
      knowledgeBaseId.value,
      testQuery.value,
      topK.value
    );
    testResults.value = response.results || response.data || [];
    showToast(
      `Found ${testResults.value.length} results`,
      testResults.value.length > 0 ? "success" : "info"
    );
  } catch (err: any) {
    console.error("Error testing retrieval:", err);
    showToast(
      err?.response?.data?.message || "Failed to test retrieval",
      "error"
    );
    testResults.value = [];
  } finally {
    isTesting.value = false;
  }
};

const getSourceTypeBadgeClass = (sourceType: string): string => {
  switch (sourceType?.toLowerCase()) {
    case "file":
    case "upload":
      return "bg-primary";
    case "url":
    case "web":
      return "bg-info";
    case "text":
      return "bg-success";
    default:
      return "bg-secondary";
  }
};

const getStatusBadgeClass = (status: string): string => {
  switch (status?.toLowerCase()) {
    case "processed":
    case "ready":
    case "completed":
      return "bg-success";
    case "processing":
      return "bg-warning";
    case "failed":
    case "error":
      return "bg-danger";
    default:
      return "bg-secondary";
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const formatSize = (bytes: number): string => {
  if (!bytes) return "N/A";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const formatScore = (score: number): string => {
  if (!score) return "0.00";
  return score.toFixed(4);
};

const showToast = (message: string, type: "success" | "error" | "info") => {
  console.log(`[${type.toUpperCase()}] ${message}`);
  // Integrate with your toast library here
};

// Lifecycle
onMounted(() => {
  loadKnowledgeBase();
  loadDocuments();
});
</script>

<style scoped>
.breadcrumb-item a {
  text-decoration: none;
  color: #6c757d;
}

.breadcrumb-item a:hover {
  color: #495057;
}

.nav-tabs .nav-link {
  color: #6c757d;
  border: none;
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef;
  color: #495057;
}

.nav-tabs .nav-link.active {
  color: #0d6efd;
  border-color: #0d6efd;
  background-color: transparent;
}

.retrieval-content {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  border-left: 3px solid #0d6efd;
  white-space: pre-wrap;
  font-family: "Courier New", Courier, monospace;
  font-size: 0.875rem;
  max-height: 200px;
  overflow-y: auto;
}

 

.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}

.shadow-hover {
  transition: all 0.3s ease;
}

.shadow-hover:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.rounded-pill {
  border-radius: 50rem !important;
}

.transition-fade {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.unit-height {
  height: 60px !important;
}

.custom-drop-zone {
  border: 2px dashed #e0e0e0;
  border-radius: 12px;
  background-color: #fcfcfc;
  height: 60px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-drop-zone:hover {
  border-color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.02);
}

.small-badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  letter-spacing: 0.5px;
}

.border-primary-light {
  border: 1px solid rgba(13, 110, 253, 0.2) !important;
}

.border-light-2 {
  border: 1px solid #eee !important;
}

.shadow-hover {
  transition: all 0.3s ease;
  border-radius: 12px !important;
}

.shadow-hover:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1) !important;
}

.input-group-text {
  border-radius: 12px 0 0 12px !important;
}

.form-control {
  border-radius: 12px;
}

.form-control.border-start-0 {
  border-radius: 0 12px 12px 0 !important;
}

.icon-circle {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(13, 202, 240, 0.15);
}

.bg-info-light {
  background-color: rgba(13, 202, 240, 0.1);
}

.max-w-500 {
  max-width: 500px;
}

.btn-group.bg-light {
  padding: 4px;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);
}

.btn-group .btn.active {
  background-color: #fff !important;
  color: #0d6efd !important;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05) !important;
}
</style>
